import { createContext, PropsWithChildren, useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { ServiceWindowCreateDto } from '../../_generated/api/structure'
import { useAction } from '../../hooks/action'
import { useProvideContext } from '../../hooks/context'
import { useMutableObject } from '../../hooks/mutable'
import { usePaginatedTable } from '../../hooks/pagination'
import ServiceWindow from '../../models/ServiceWindow'
import TableControl from '../../models/TableControl'
import { mapReadResponse, mapReadTableResponse, throwIfResponseNotOk } from '../../tools/api'
import t from '../../tools/t'
import { useApiContext } from './ApiContext'
import { useToastContext } from './ToastContext'
import { useUserContext } from './UserContext'

interface ServiceWindowContextValue {
    windows: TableControl<ServiceWindow>
    loading: boolean
    loadError?: Error
    selected?: ServiceWindow
    selectedLoading: boolean
    selectedError?: Error
    next?: ServiceWindow
    nextLoading: boolean
    nextError?: Error
    saving: boolean
    refresh: () => Promise<void>
    save: (
        data: ServiceWindowCreateDto,
        existing?: ServiceWindow
    ) => Promise<ServiceWindow | undefined>
    complete: (window: ServiceWindow) => Promise<ServiceWindow | undefined>
}

const ServiceWindowContext = createContext<ServiceWindowContextValue | undefined>(undefined)

// Reset service data and in-flight view state when the tenant or selected entity changes.
export default function ServiceWindowProvider({ children }: PropsWithChildren) {
    const { selectedOrganization } = useUserContext()
    const { serviceWindowId, boltConnectionId } = useParams()
    return (
        <TenantServiceWindowProvider
            key={`${selectedOrganization?.id}:${serviceWindowId}:${boltConnectionId}`}
        >
            {children}
        </TenantServiceWindowProvider>
    )
}

function TenantServiceWindowProvider({ children }: PropsWithChildren) {
    const { serviceWindowApi } = useApiContext()
    const { selectedOrganization } = useUserContext()
    const tenantId = selectedOrganization?.id
    const { serviceWindowId, boltConnectionId } = useParams()
    const { pathname } = useLocation()
    const onServicePage = pathname.startsWith('/service-window')
    const Toast = useToastContext()
    const [loadError, setLoadError] = useState<Error>()
    const [selectedError, setSelectedError] = useState<Error>()
    const [nextError, setNextError] = useState<Error>()

    const [windows, loading, , refreshWindows] = usePaginatedTable(
        async (table: TableControl<ServiceWindow>) => {
            if (!tenantId || !onServicePage) return null
            try {
                const response = await serviceWindowApi.getAllServiceWindowsV3({
                    tenantId,
                    skip: table.skip,
                    take: Math.min(table.take, 100),
                })
                const result = mapReadTableResponse(response, table, ServiceWindow)
                setLoadError(undefined)
                return result
            } catch (error) {
                setLoadError(error as Error)
                throw error
            }
        },
        [tenantId, onServicePage]
    )

    const [selection, selectedLoading, , refreshSelected, replaceSelected] =
        useMutableObject(async () => {
            if (!tenantId || !serviceWindowId) return { id: serviceWindowId, window: undefined }
            try {
                // The API has no get-by-id operation. Follow server pagination for bookmarked windows.
                for (let skip = 0; ; skip += 100) {
                    const response = await serviceWindowApi.getAllServiceWindowsV3({
                        tenantId,
                        skip,
                        take: 100,
                    })
                    throwIfResponseNotOk(response)
                    const dto = response.data.items.find((item) => item.id === serviceWindowId)
                    if (dto) {
                        setSelectedError(undefined)
                        return { id: serviceWindowId, window: new ServiceWindow(dto) }
                    }
                    if (!response.data.hasNextPage) throw new Error(t('service_window_not_found'))
                }
            } catch (error) {
                setSelectedError(error as Error)
                throw error
            }
        }, [tenantId, serviceWindowId])

    const [nextResult, nextLoading, , refreshNext] = useMutableObject(async () => {
        if (!tenantId || !boltConnectionId) return { id: boltConnectionId, window: undefined }
        try {
            const response = await serviceWindowApi.getNextServiceWindowByBoltConnectionV3({
                tenantId,
                boltConnectionId,
            })
            throwIfResponseNotOk(response)
            setNextError(undefined)
            return {
                id: boltConnectionId,
                window: response.status === 204 ? undefined : new ServiceWindow(response.data),
            }
        } catch (error) {
            setNextError(error as Error)
            throw error
        }
    }, [tenantId, boltConnectionId])

    const refresh = async () => {
        await Promise.all([refreshWindows(), refreshSelected(), refreshNext()])
    }

    const [saveLoading, saveError, save] = useAction(
        async (data: ServiceWindowCreateDto, existing?: ServiceWindow) => {
            if (!tenantId) return
            const response = existing
                ? await serviceWindowApi.updateServiceWindowV3({
                      tenantId,
                      serviceWindowId: existing.id,
                      serviceWindowUpdateDto: { ...data, revision: existing.data.revision },
                  })
                : await serviceWindowApi.createServiceWindowV3({
                      tenantId,
                      serviceWindowCreateDto: data,
                  })
            const result = mapReadResponse(response, ServiceWindow)
            if (result.id === serviceWindowId) replaceSelected({ id: result.id, window: result })
            await Promise.all([refreshWindows(), refreshNext()])
            Toast.success(t('service_window_saved'), result.name)
            return result
        }
    )

    const [completeLoading, completeError, complete] = useAction(async (window: ServiceWindow) => {
        if (!tenantId || !window.canComplete || !window.data.nextService) return
        const response = await serviceWindowApi.completeServiceWindowV3({
            tenantId,
            serviceWindowId: window.id,
            serviceWindowCompleteDto: { occurrenceId: window.data.nextService.occurrenceId },
        })
        const result = mapReadResponse(response, ServiceWindow)
        await refresh()
        Toast.success(t('service_window_completed'), result.name)
        return result
    })

    useEffect(() => {
        const error = saveError || completeError
        if (error) Toast.error(error, t('service_window_error'))
    }, [saveError, completeError])

    return (
        <ServiceWindowContext.Provider
            value={{
                windows,
                loading,
                loadError,
                selected: selection?.id === serviceWindowId ? selection?.window : undefined,
                selectedLoading,
                selectedError,
                next: nextResult?.id === boltConnectionId ? nextResult?.window : undefined,
                nextLoading,
                nextError,
                saving: saveLoading || completeLoading,
                refresh,
                save,
                complete,
            }}
        >
            {children}
        </ServiceWindowContext.Provider>
    )
}

export const useServiceWindowContext = () =>
    useProvideContext(ServiceWindowContext, 'ServiceWindow')
